import React, { useState, useEffect, useCallback, useRef } from 'react';
import packageService from '../../services/packageService';
import profileService from '../../services/profileService';
import masterService from '../../services/masterService';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';

// Sub-components
import PackageHierarchyBanner from './package/PackageHierarchyBanner';
import PackageDetailsStep from './package/PackageDetailsStep';
import PackageComponentsStep from './package/PackageComponentsStep';
import PackageReviewStep from './package/PackageReviewStep';
import PackageDetailDrawer from './package/PackageDetailDrawer';
import PackagePanelMappingSection from './package/PackagePanelMappingSection';

// Styles
import './MastersHub.css';
import './PackageMasterPage.css';

const INITIAL_PACKAGE = {
  packageId: 0,
  packageCode: '',
  packageName: '',
  description: '',
  displayOrder: 0,
  isActive: true
};

const noopToast = () => {};

export const PackageMasterPage = ({ showToast = noopToast }) => {
  const toastRef = useRef(showToast);
  useEffect(() => {
    toastRef.current = showToast;
  }, [showToast]);

  const notify = useCallback((msg, type = 'info') => {
    if (toastRef.current) {
      toastRef.current(msg, type);
    }
  }, []);

  // Mode: 'builder' (Form-First) | 'list'
  const [viewMode, setViewMode] = useState('builder');
  const [builderStep, setBuilderStep] = useState(1); // 1: Details, 2: Components, 3: Review
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_PACKAGE);
  const [errors, setErrors] = useState({});

  // Selected Components: array of { componentType: 'TEST'|'PROFILE', componentId, code, name, departmentName, testCount }
  const [selectedComponents, setSelectedComponents] = useState([]);

  // Catalog State
  const [allTests, setAllTests] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  // Package List State
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Centralized Pagination Hook
  const { paginatedItems: paginatedPackages, paginationProps: packagePaginationProps } = usePagination({
    items: packages,
    resetDeps: [debouncedSearch, statusFilter]
  });

  // Drawers & Modals
  const [inspectPackage, setInspectPackage] = useState(null);
  const [panelMappingPackage, setPanelMappingPackage] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load Catalogs: Investigations and Profiles (with enriched component counts)
  const loadCatalogs = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const [testsRes, profilesRes] = await Promise.all([
        masterService.getInvestigations({ isActive: true, pageSize: 1000 }),
        profileService.getProfiles({ isActive: true })
      ]);

      if (testsRes && testsRes.success) {
        setAllTests(testsRes.data?.items || testsRes.data || []);
      }
      if (profilesRes && profilesRes.success) {
        setAllProfiles(profilesRes.data || []);
      }
    } catch (err) {
      console.error('Error loading package catalogs:', err);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  // Load Packages List
  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.isActive = statusFilter === 'active';
      }
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const res = await packageService.getPackages(params);
      if (res && res.success) {
        setPackages(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
      notify(err.response?.data?.message || 'Failed to load packages', 'error');
    } finally {
      setLoadingPackages(false);
    }
  }, [debouncedSearch, statusFilter, notify]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadPackages();
    }
  }, [viewMode, loadPackages]);

  // Field change handler for Step 1
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  // Step 1 Validation
  const validateDetailsStep = () => {
    const errs = {};
    if (!formData.packageName.trim()) {
      errs.packageName = 'Package name is required';
    }
    if (!formData.packageCode.trim()) {
      errs.packageCode = 'Package code is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNextFromDetails = () => {
    if (validateDetailsStep()) {
      setBuilderStep(2);
    } else {
      notify('Please complete the required package details.', 'warning');
    }
  };

  const handleNextFromComponents = () => {
    if (selectedComponents.length === 0) {
      notify('Please select at least one Test or Profile before reviewing.', 'warning');
      return;
    }
    setBuilderStep(3);
  };

  // Component manipulation
  const handleAddComponent = (item) => {
    setSelectedComponents(prev => [...prev, item]);
  };

  const handleRemoveComponent = (item) => {
    setSelectedComponents(prev =>
      prev.filter(c => !(c.componentType === item.componentType && c.componentId === item.componentId))
    );
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    setSelectedComponents(prev => {
      const copy = [...prev];
      const temp = copy[index - 1];
      copy[index - 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
  };

  const handleMoveDown = (index) => {
    setSelectedComponents(prev => {
      if (index >= prev.length - 1) return prev;
      const copy = [...prev];
      const temp = copy[index + 1];
      copy[index + 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
  };

  // Switch to clean New Package Builder
  const handleStartNewPackage = () => {
    setFormData(INITIAL_PACKAGE);
    setSelectedComponents([]);
    setErrors({});
    setIsEditMode(false);
    setBuilderStep(1);
    setViewMode('builder');
  };

  // Edit existing package
  const handleEditPackage = async (pkg) => {
    setIsEditMode(true);
    setFormData({
      packageId: pkg.packageId,
      packageCode: pkg.packageCode || '',
      packageName: pkg.packageName || '',
      description: pkg.description || '',
      displayOrder: pkg.displayOrder || 0,
      isActive: pkg.isActive
    });
    setErrors({});
    setBuilderStep(1);

    try {
      const res = await packageService.getPackageComponents(pkg.packageId);
      if (res && res.success) {
        const comps = (res.data || []).map(c => ({
          componentType: c.componentType,
          componentId: c.componentId,
          code: c.componentCode,
          name: c.componentName,
          departmentName: c.departmentName,
          testCount: c.testCount || 1
        }));
        setSelectedComponents(comps);
      }
    } catch (err) {
      console.error('Error loading package components:', err);
      notify('Could not load existing components', 'error');
    }

    setViewMode('builder');
  };

  // Final Save Handler (Step 3)
  const handleSavePackage = async () => {
    setSaving(true);
    try {
      const payload = {
        packageCode: formData.packageCode.trim().toUpperCase(),
        packageName: formData.packageName.trim(),
        description: formData.description?.trim() || null,
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        components: selectedComponents.map((c, idx) => ({
          componentType: c.componentType,
          componentId: c.componentId,
          displayOrder: idx + 1,
          isMandatory: true
        }))
      };

      if (isEditMode) {
        await packageService.updatePackage(formData.packageId, {
          ...payload,
          isActive: formData.isActive
        });
        notify(`Package "${formData.packageName}" updated successfully.`, 'success');
      } else {
        const created = await packageService.createPackage(payload);
        notify(`Health Package "${formData.packageName}" created successfully!`, 'success');
        // Offer panel mapping for newly created package
        setPanelMappingPackage({
          packageId: created?.packageId || 0,
          packageName: formData.packageName,
          packageCode: formData.packageCode
        });
      }

      setViewMode('list');
      loadPackages();
    } catch (err) {
      console.error('Error saving package:', err);
      notify(err.response?.data?.message || 'Error saving package', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Status
  const handleToggleStatus = (pkg) => {
    setConfirmTarget({
      packageId: pkg.packageId,
      packageName: pkg.packageName,
      nextStatus: !pkg.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await packageService.updatePackageStatus(confirmTarget.packageId, confirmTarget.nextStatus);
      notify(`Package ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadPackages();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  // Table Columns for List View
  const listColumns = [
    {
      key: 'package',
      header: 'Package Title & Code',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <strong
            style={{ color: 'var(--accent, #0284c7)', cursor: 'pointer', fontSize: '0.9rem' }}
            onClick={() => setInspectPackage(row)}
            title="Click to view full package composition"
          >
            {row.packageName}
          </strong>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <span className="pkg-badge-package">{row.packageCode}</span>
            {row.description && (
              <span style={{ fontSize: '0.75rem', color: '#64748b' }} title={row.description}>
                {row.description.length > 50 ? `${row.description.slice(0, 50)}...` : row.description}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'profiles',
      header: 'Profiles',
      width: '110px',
      render: (row) => (
        <span className="pkg-badge-profile">
          {row.profileCount || 0} Profiles
        </span>
      )
    },
    {
      key: 'tests',
      header: 'Direct Tests',
      width: '120px',
      render: (row) => (
        <span className="pkg-badge-test">
          {row.testCount || 0} Tests
        </span>
      )
    },
    {
      key: 'totalComponents',
      header: 'Total Items',
      width: '100px',
      render: (row) => (
        <strong style={{ color: '#0f172a' }}>
          {row.activeComponentCount ?? ((row.profileCount || 0) + (row.testCount || 0))}
        </strong>
      )
    },
    {
      key: 'order',
      header: 'Order',
      width: '70px',
      render: (row) => row.displayOrder || 0
    },
    {
      key: 'isActive',
      header: 'Status',
      width: '100px',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '130px',
      align: 'right',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="pkg-btn-secondary-xs"
            onClick={() => setInspectPackage(row)}
            title="Inspect composition"
          >
            👁️ View
          </button>
          <button
            type="button"
            className="pkg-btn-secondary-xs"
            onClick={() => handleEditPackage(row)}
            title="Edit Package"
          >
            ✏️
          </button>
          <ActionMenu
            actions={[
              {
                label: 'Panel Tariffs & Rates',
                icon: '🏛️',
                onClick: () => setPanelMappingPackage(row)
              },
              {
                label: row.isActive ? 'Deactivate Package' : 'Activate Package',
                icon: row.isActive ? '⏸️' : '▶️',
                onClick: () => handleToggleStatus(row)
              }
            ]}
          />
        </div>
      )
    }
  ];

  return (
    <div className="page-container fade-in">
      {/* Visual Hierarchy Banner */}
      <PackageHierarchyBanner compact={viewMode === 'builder'} />

      {viewMode === 'builder' ? (
        /* ================= FORM-FIRST PACKAGE BUILDER ================= */
        <div className="pkg-wizard-wrapper">
          {/* Wizard Header with Stepper */}
          <div className="pkg-wizard-header">
            <div className="pkg-wizard-top">
              <div className="pkg-wizard-title-group">
                <h2>
                  <span>📦</span> {isEditMode ? `Edit Package: ${formData.packageName}` : 'Package Builder'}
                </h2>
                <p>Create a package by selecting reusable profiles and individual clinical tests.</p>
              </div>

              <div className="pkg-wizard-top-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewMode('list')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  📋 View All Packages ({packages.length})
                </button>
              </div>
            </div>

            {/* Stepper Bar */}
            <div className="pkg-stepper">
              <div
                className={`pkg-step-item ${builderStep === 1 ? 'active' : builderStep > 1 ? 'completed' : ''}`}
                onClick={() => setBuilderStep(1)}
              >
                <span className="pkg-step-num">{builderStep > 1 ? '✓' : '1'}</span>
                <span>Package Details</span>
              </div>

              <span className="pkg-step-divider">→</span>

              <div
                className={`pkg-step-item ${builderStep === 2 ? 'active' : builderStep > 2 ? 'completed' : ''}`}
                onClick={() => {
                  if (validateDetailsStep()) setBuilderStep(2);
                }}
              >
                <span className="pkg-step-num">{builderStep > 2 ? '✓' : '2'}</span>
                <span>Add Components ({selectedComponents.length})</span>
              </div>

              <span className="pkg-step-divider">→</span>

              <div
                className={`pkg-step-item ${builderStep === 3 ? 'active' : ''} ${selectedComponents.length === 0 ? 'disabled' : ''}`}
                onClick={() => {
                  if (validateDetailsStep() && selectedComponents.length > 0) setBuilderStep(3);
                }}
              >
                <span className="pkg-step-num">3</span>
                <span>Review & Save</span>
              </div>
            </div>
          </div>

          {/* Wizard Body Steps */}
          {builderStep === 1 && (
            <PackageDetailsStep
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
              isEditMode={isEditMode}
              onNext={handleNextFromDetails}
              onCancel={() => setViewMode('list')}
            />
          )}

          {builderStep === 2 && (
            <PackageComponentsStep
              allTests={allTests}
              allProfiles={allProfiles}
              loadingCatalog={loadingCatalog}
              selectedComponents={selectedComponents}
              onAddComponent={handleAddComponent}
              onRemoveComponent={handleRemoveComponent}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onBack={() => setBuilderStep(1)}
              onNext={handleNextFromComponents}
            />
          )}

          {builderStep === 3 && (
            <PackageReviewStep
              formData={formData}
              selectedComponents={selectedComponents}
              allProfiles={allProfiles}
              isEditMode={isEditMode}
              saving={saving}
              onBack={() => setBuilderStep(2)}
              onSave={handleSavePackage}
            />
          )}
        </div>
      ) : (
        /* ================= PACKAGE DIRECTORY LIST ================= */
        <div className="pkg-list-wrapper fade-in">
          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div className="page-title-group">
              <h1 className="page-title">Health Package Directory</h1>
              <span className="page-subtitle">
                Directory of all configured health packages with profile groupings, direct tests, and tariff links.
              </span>
            </div>

            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartNewPackage}
                id="pkg-create-new-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <span>➕</span> Create New Package
              </button>
            </div>
          </div>

          <MasterToolbar
            searchPlaceholder="Search packages by code or name... (Ctrl+K)"
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Packages' },
                  { value: 'inactive', label: 'Inactive Packages' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={listColumns}
            data={paginatedPackages}
            loading={loadingPackages}
            emptyTitle="No Health Packages Configured"
            emptyDescription="Create your first health checkup package combining clinical profiles and individual tests."
            onRowClick={(row) => setInspectPackage(row)}
            pagination={packagePaginationProps}
          />
        </div>
      )}

      {/* Slide-out Composition Detail Drawer */}
      {inspectPackage && (
        <PackageDetailDrawer
          packageItem={inspectPackage}
          onClose={() => setInspectPackage(null)}
          onEdit={(pkg) => {
            setInspectPackage(null);
            handleEditPackage(pkg);
          }}
          onOpenPanelMapping={(pkg) => {
            setInspectPackage(null);
            setPanelMappingPackage(pkg);
          }}
        />
      )}

      {/* Stage 2: Panel Mapping Modal */}
      {panelMappingPackage && (
        <PackagePanelMappingSection
          packageData={panelMappingPackage}
          onClose={() => setPanelMappingPackage(null)}
        />
      )}

      {/* Confirm Status Toggle Modal */}
      {confirmTarget && (
        <ConfirmModal
          title={confirmTarget.nextStatus ? 'Activate Health Package' : 'Deactivate Health Package'}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} "${confirmTarget.packageName}"? Inactive packages cannot be ordered on patient registrations.`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate Package' : 'Deactivate Package'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
};

export default PackageMasterPage;
